import React,{useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../../../contexts/AuthContext';
import {useData} from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import ConflictCheckModal from '../components/ConflictCheckModal';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import InvestigatorSLADashboard from '../components/InvestigatorSLADashboard';

const {FiBriefcase,FiUsers,FiFileText,FiSettings,FiLogOut,FiGlobe,FiCheckCircle,FiClock,FiDollarSign,FiBarChart2,FiMenu,FiX,FiSave,FiLock,FiUser,FiInbox,FiExternalLink,FiAlertCircle,FiShield,FiCalendar,FiActivity}=FiIcons;

const UtrederDashboard=()=> {
  const {user,logout}=useAuth();
  const {reports, companies} = useData();
  const navigate=useNavigate();
  const [activeView,setActiveView]=useState('overview');
  const [isMobileMenuOpen,setIsMobileMenuOpen]=useState(false);
  const [assignments,setAssignments]=useState([]);
  const [loading,setLoading]=useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [utrederData, setUtrederData] = useState(null);

  // VIKTIG: Filter saker som er tildelt denne utrederen
  const legacyAssignedReports = reports.filter(r => r.assignedInvestigatorId === user?.id || (user?.role === 'utreder' && r.externalHandling));

  useEffect(() => {
    if (user?.email) {
      fetchAssignments();
    }
  }, [user]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const { data: utreder, error: utrederError } = await supabase.from('utredere').select('*').eq('email', user.email).single();
      if (utrederError) throw utrederError;
      setUtrederData(utreder);
      const { data: assigned, error: assignError } = await supabase.from('utreder_assignments').select(`*, reports (id, title, description, category, severity, status, created_at, company_id, deadline_ack, deadline_feedback, is_confirmed, contradiction_status)`).eq('utreder_id', utreder.id).order('assigned_at', { ascending: false });
      if (assignError) throw assignError;
      setAssignments(assigned || []);
    } catch (err) {
      console.error('Error fetching assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartCase = (assignment) => {
    if (assignment.conflict_check_status === 'pending') {
      setSelectedAssignment(assignment);
      setShowConflictModal(true);
    } else {
      navigate(`/company/investigation/form/${assignment.report_id}`);
    }
  };

  const menuItems=[ 
    {id: 'overview',label: 'Oversikt',icon: FiBarChart2},
    {id: 'cases',label: 'Tildelte Saker',icon: FiFileText,badge: assignments.length || legacyAssignedReports.length},
    {id: 'sla',label: 'SLA & Frister',icon: FiActivity},
    {id: 'calendar',label: 'Tilgjengelighet',icon: FiCalendar},
    {id: 'mottatte-saker',label: 'Egen Innboks',icon: FiInbox},
    {id: 'microsite',label: 'Microsite',icon: FiGlobe},
    {id: 'settings',label: 'Innstillinger',icon: FiSettings},
  ];

  const getCompanyName = (id) => companies.find(c => c.id === id)?.name || id;

  const stats = {
    active: assignments.filter(a => a.status === 'in_progress').length,
    pending: assignments.filter(a => a.status === 'assigned').length,
    completed: assignments.filter(a => a.status === 'completed').length,
    honorary: '45.000 kr'
  };

  return ( 
    <div className="flex h-screen bg-gray-50 font-sans text-gray-900 overflow-hidden"> 
      {/* Sidebar */} 
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out flex flex-col ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}> 
        <div className="p-6 border-b border-gray-100 flex items-center gap-3"> 
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white"><SafeIcon icon={FiBriefcase} /></div> 
          <div> 
            <h1 className="text-sm font-bold text-gray-900">Utrederportal</h1> 
            <p className="text-xs text-gray-500">{user?.name}</p> 
          </div> 
          <button className="md:hidden ml-auto" onClick={()=> setIsMobileMenuOpen(false)}><SafeIcon icon={FiX} /></button> 
        </div> 
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1"> 
          {menuItems.map(item=> ( 
            <button key={item.id} onClick={()=> {setActiveView(item.id);setIsMobileMenuOpen(false);if (item.id==='microsite') navigate('/utreder/microsite');if (item.id==='mottatte-saker') navigate('/utreder/mottatte-saker');}} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeView===item.id ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`} > 
              <div className="flex items-center gap-3"><SafeIcon icon={item.icon} /> <span>{item.label}</span></div> 
              {item.badge > 0 && <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">{item.badge}</span>} 
            </button> 
          ))} 
        </nav> 
        <div className="p-4 border-t border-gray-100 space-y-2"> 
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"><SafeIcon icon={FiLogOut} /> Logg ut</button> 
        </div> 
      </aside> 
      {/* Main Content */} 
      <main className="flex-1 flex flex-col overflow-hidden"> 
        <header className="md:hidden bg-white border-b p-4 flex justify-between items-center"> 
          <button onClick={()=> setIsMobileMenuOpen(true)}><SafeIcon icon={FiMenu} className="text-2xl" /></button> 
          <span className="font-bold">Dashboard</span> 
        </header> 
        <div className="flex-1 overflow-y-auto p-6 md:p-8"> 
          <AnimatePresence mode="wait"> 
            {activeView==='overview' && ( 
              <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="space-y-6"> 
                <h2 className="text-2xl font-bold text-gray-900">Oversikt</h2> 
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6"> 
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"> 
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Aktive Oppdrag</p> 
                    <p className="text-3xl font-black text-indigo-600">{stats.active}</p> 
                  </div> 
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"> 
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Nye Forespørsler</p> 
                    <p className="text-3xl font-black text-amber-500">{stats.pending}</p> 
                  </div> 
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"> 
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Fullførte Saker</p> 
                    <p className="text-3xl font-black text-green-600">{stats.completed}</p> 
                  </div> 
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"> 
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Honorar (Est.)</p> 
                    <p className="text-3xl font-black text-gray-900">{stats.honorary}</p> 
                  </div> 
                </div> 
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"> 
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"> <SafeIcon icon={FiClock} className="text-indigo-600" /> Siste aktivitet </h3> 
                  <div className="space-y-4"> 
                    {assignments.slice(0,5).map(a=> ( 
                      <div key={a.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100 hover:bg-indigo-50/30 transition-all group"> 
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${a.status === 'assigned' ? 'bg-amber-100 text-amber-600' : 'bg-indigo-100 text-indigo-600'}`}>
                            <SafeIcon icon={a.status === 'assigned' ? FiAlertCircle : FiFileText} />
                          </div>
                          <div> 
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-gray-900">{a.reports?.title || 'Uten tittel'}</p> 
                              {a.priority === 'høy' && <span className="bg-red-100 text-red-600 text-[8px] font-black uppercase px-2 py-0.5 rounded">Haster</span>}
                            </div>
                            <p className="text-xs text-gray-500">Kunde: {getCompanyName(a.reports?.company_id)}</p> 
                          </div>
                        </div> 
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{new Date(a.assigned_at).toLocaleDateString()}</span>
                          <button onClick={() => handleStartCase(a)} className="text-[10px] font-black uppercase text-indigo-600 bg-white px-3 py-1.5 rounded border border-indigo-100 shadow-sm hover:bg-indigo-600 hover:text-white transition-all"> {a.conflict_check_status === 'pending' ? 'Vurder Habilitet' : 'Åpne saksmappe'} </button>
                        </div>
                      </div> 
                    ))} 
                    {assignments.length === 0 && legacyAssignedReports.length === 0 && <p className="text-center py-10 text-gray-400 italic">Ingen tildelte saker ennå.</p>}
                  </div> 
                </div> 
              </motion.div> 
            )} 
            {activeView==='cases' && ( 
              <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="space-y-6"> 
                <h2 className="text-2xl font-bold text-gray-900">Saksmappe</h2> 
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"> 
                  <table className="w-full text-left"> 
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500 font-bold"> 
                      <tr> 
                        <th className="px-6 py-4">Sakstittel</th> 
                        <th className="px-6 py-4">Bedrift</th> 
                        <th className="px-6 py-4">Status</th> 
                        <th className="px-6 py-4">Prioritet</th> 
                        <th className="px-6 py-4 text-right">Handling</th> 
                      </tr> 
                    </thead> 
                    <tbody className="divide-y divide-gray-100"> 
                      {assignments.map(a=> ( 
                        <tr key={a.id} className="hover:bg-gray-50 group"> 
                          <td className="px-6 py-4">
                            <div className="font-bold text-gray-900">{a.reports?.title || 'Uten tittel'}</div>
                            <div className="text-[10px] text-gray-400 font-medium">{a.report_id}</div>
                          </td> 
                          <td className="px-6 py-4 text-sm text-gray-600">{getCompanyName(a.reports?.company_id)}</td> 
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 text-[10px] font-black uppercase rounded-full border ${a.status === 'assigned' ? 'bg-amber-50 text-amber-600 border-amber-100' : a.status === 'in_progress' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-green-50 text-green-600 border-green-100'}`}>
                              {a.status === 'assigned' ? 'Venter' : a.status === 'in_progress' ? 'Pågår' : 'Fullført'}
                            </span>
                          </td> 
                          <td className="px-6 py-4"><span className={`text-[10px] font-black uppercase ${a.priority === 'høy' ? 'text-red-500' : 'text-gray-400'}`}>{a.priority || 'standard'}</span></td> 
                          <td className="px-6 py-4 text-right"> 
                            <button onClick={() => handleStartCase(a)} className="text-indigo-600 hover:text-indigo-900 text-xs font-black uppercase tracking-widest">{a.conflict_check_status === 'pending' ? 'Habilitetssjekk' : 'Utfør utredning'}</button> 
                          </td> 
                        </tr> 
                      ))} 
                      {assignments.length === 0 && legacyAssignedReports.map(c=> ( 
                        <tr key={c.id} className="hover:bg-gray-50"> 
                          <td className="px-6 py-4 font-medium">{c.title}</td> 
                          <td className="px-6 py-4 text-sm text-gray-600">{getCompanyName(c.companyId)}</td> 
                          <td className="px-6 py-4"><span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-[10px] font-black uppercase rounded-full">Tildelt</span></td> 
                          <td className="px-6 py-4">-</td> 
                          <td className="px-6 py-4 text-right"> 
                            <button onClick={() => navigate(`/company/investigation/form/${c.id}`)} className="text-indigo-600 hover:underline text-sm font-bold">Utfør utredning</button> 
                          </td> 
                        </tr> 
                      ))} 
                    </tbody> 
                  </table> 
                </div> 
              </motion.div> 
            )} 
            {activeView === 'sla' && (
              <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">SLA & Frister</h2>
                <InvestigatorSLADashboard assignments={assignments} onSelectCase={handleStartCase} />
              </motion.div>
            )}
            {activeView === 'calendar' && utrederData && (
              <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">Tilgjengelighet</h2>
                <AvailabilityCalendar utrederId={utrederData.id} />
              </motion.div>
            )}
            {activeView==='settings' && ( 
              <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="max-w-3xl"> 
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Innstillinger</h2> 
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6"> 
                  <div className="p-6 border-b border-gray-100"> 
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2"> 
                      <SafeIcon icon={FiUser} className="text-indigo-600" /> Min Profil 
                    </h3> 
                  </div> 
                  <div className="p-6 space-y-4"> 
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> 
                      <div> 
                        <label className="block text-sm font-medium text-gray-700 mb-1">Navn</label> 
                        <input type="text" defaultValue={user?.name} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" /> 
                      </div> 
                      <div> 
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tittel</label> 
                        <input type="text" placeholder="Senior Utreder" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" /> 
                      </div> 
                    </div> 
                    <div className="flex justify-end pt-2"> 
                      <button className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"> 
                        <SafeIcon icon={FiSave} /> Lagre endringer 
                      </button> 
                    </div> 
                  </div> 
                </div> 
              </motion.div> 
            )} 
          </AnimatePresence> 
        </div> 
      </main> 
      <AnimatePresence>
        {showConflictModal && selectedAssignment && (
          <ConflictCheckModal
            isOpen={showConflictModal}
            onClose={() => setShowConflictModal(false)}
            assignment={selectedAssignment}
            onComplete={() => {
              setShowConflictModal(false);
              fetchAssignments();
            }}
          />
        )}
      </AnimatePresence>
    </div> 
  );
};

export default UtrederDashboard;